import { useRef } from 'react';
import { Template } from '../../../mocks/websiteTemplates';

interface RecentlyViewedProps {
  templates: Template[];
  onPreview: (template: Template) => void;
}

const CATEGORY_DOTS: Record<string, string> = {
  'E-commerce': 'bg-emerald-400',
  'SaaS & Tech': 'bg-violet-400',
  'Portfolio & Creative': 'bg-pink-400',
  'Restaurant & Food': 'bg-orange-400',
  'Health & Wellness': 'bg-green-400',
  'Real Estate': 'bg-cyan-400',
  'Education': 'bg-amber-400',
  'Non-Profit': 'bg-red-400',
  'Fashion & Beauty': 'bg-fuchsia-400',
  'Travel & Hospitality': 'bg-sky-400',
  'Finance & Legal': 'bg-slate-400',
  'Entertainment & Media': 'bg-purple-400',
};

export default function RecentlyViewed({ templates, onPreview }: RecentlyViewedProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  if (templates.length === 0) return null;

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -320, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' });
  };

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-5 h-5 flex items-center justify-center rounded-md bg-amber-50 border border-amber-200">
            <i className="ri-history-line text-amber-600" style={{ fontSize: 11 }}></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Recently Viewed</h3>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-medium">
            {templates.length}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={scrollLeft}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          <button
            onClick={scrollRight}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {templates.map((template, idx) => (
          <div
            key={`${template.name}-${idx}`}
            onClick={() => onPreview(template)}
            className="flex-shrink-0 w-52 bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-violet-400 transition-all duration-200 cursor-pointer group"
          >
            {/* Thumbnail */}
            <div className="w-full relative overflow-hidden" style={{ height: 100 }}>
              <img
                src={template.image}
                alt={template.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              {/* Hover eye */}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                  <i className="ri-eye-line text-gray-700 text-sm"></i>
                </div>
              </div>
              {/* Badge */}
              {template.badge && (
                <div className={`absolute top-2 right-2 px-1.5 py-0.5 text-white text-xs font-semibold rounded-full ${
                  template.badge === 'Popular' ? 'bg-amber-500/90' : 'bg-emerald-500/90'
                }`}>
                  {template.badge === 'Popular' ? '🔥' : '✦'}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="px-3 py-2.5">
              <p className="text-xs font-semibold text-gray-900 truncate mb-1">{template.name}</p>
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${CATEGORY_DOTS[template.category] ?? 'bg-gray-400'}`}></div>
                <p className="text-xs text-gray-500 truncate">{template.category}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
